import type { AuthCheckParams } from '~/modules/nuxt3-module-doyac/composables/auth'
import type { DYAMenuItem } from '../components/DYAMenu.vue'

export interface DYAMenuSearchResult {
  /** 매칭된 메뉴 항목 */
  item: DYAMenuItem
  /** 상위 그룹 경로 (예: '결제관리 > 무통장입금'의 '결제관리') */
  path: string
}

/** 공백/대소문자를 무시한 비교용 문자열로 변환 */
export const normalizeDYAMenuKeyword = (text?: string | null): string => {
  return (text ?? '').toLowerCase().replace(/\s+/g, '')
}

/**
 * 메뉴 항목의 권한 체크.
 * 자식이 있는 경우 자식이 모두 권한이 없으면 부모도 권한이 없는 것으로 간주한다.
 *
 * @param item 체크할 메뉴 항목
 * @param check useDYAuth().hasAuth
 */
export const hasDYAMenuAuth = (
  item: DYAMenuItem,
  check: (scope: string | AuthCheckParams | boolean) => boolean
): boolean => {
  // 자식 모두가 권한이 없을 경우 부모도 권한이 없는 것과 같음
  if (item.children && item.children.length > 0) {
    // 부모 자신의 권한을 먼저 체크
    const itemHasAuth = item.auth === undefined || check(item.auth)
    return itemHasAuth && item.children.some(entry => hasDYAMenuAuth(entry, check))
  }

  // 권한 설정을 따로 하지 않았다면 권한 있음으로 간주
  if (item.auth === undefined) {
    return true
  }

  return check(item.auth)
}

/**
 * 메뉴 트리를 평탄화하여 키워드에 매칭되는 "이동 가능한(to/href가 있는)" 메뉴만 반환한다.
 *
 * - 상위 그룹명이 매칭되면 그 하위 메뉴 전체가 결과에 포함된다. (예: '결제관리' 검색 → 하위 3개 모두)
 * - 헤더(isHeader)와 권한이 없는 항목은 제외된다.
 * - to/href + 제목이 같은 중복 항목은 한 번만 반환된다.
 *
 * @param menu 검색 대상 메뉴 트리
 * @param keyword 검색어
 * @param hasAuth 권한 체크 함수 (미지정 시 전부 허용)
 */
export const searchDYAMenu = (
  menu: DYAMenuItem[],
  keyword?: string | null,
  hasAuth: (item: DYAMenuItem) => boolean = () => true
): DYAMenuSearchResult[] => {
  const normalizedKeyword = normalizeDYAMenuKeyword(keyword)
  if (!normalizedKeyword) return []

  const results: DYAMenuSearchResult[] = []
  const found = new Set<string>()

  const walk = (list: DYAMenuItem[], titles: string[], parentMatched: boolean) => {
    for (const item of list) {
      if (item.isHeader || !hasAuth(item)) continue

      const matched = parentMatched || normalizeDYAMenuKeyword(item.title).includes(normalizedKeyword)

      if (item.children && item.children.length > 0) {
        walk(item.children, [...titles, item.title], matched)
        continue
      }

      if (!matched || (!item.to && !item.href)) continue

      const key = `${item.to ?? item.href}|${item.title}`
      if (found.has(key)) continue
      found.add(key)

      results.push({ item, path: titles.join(' > ') })
    }
  }

  walk(menu, [], false)

  return results
}
